/**
 * useVerticalAccent — applies the admin's per-vertical accent override.
 *
 * On every route change, looks at the URL path:
 *   /movies/* → accent = vertical_accents.movies || category-2
 *   /events/* → accent = vertical_accents.events || accent
 *   etc.
 *
 * Sets --snt-vertical-accent CSS variable on document root.
 * Components opt-in by using `var(--snt-vertical-accent, var(--snt-accent))`.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const VERTICAL_PATTERNS = [
  { match: /^\/movies(\/|$)/,      vertical: 'movies' },
  { match: /^\/events(\/|$)/,      vertical: 'events' },
  { match: /^\/e\/[^/]+/,          vertical: 'events' },
  { match: /^\/plays(\/|$)/,       vertical: 'plays' },
  { match: /^\/sports(\/|$)/,      vertical: 'sports' },
  { match: /^\/attractions(\/|$)/, vertical: 'attractions' },
  { match: /^\/workshops(\/|$)/,   vertical: 'attractions' },
  { match: /^\/festivals(\/|$)/,   vertical: 'attractions' },
  { match: /^\/stream(\/|$)/,      vertical: 'stream' },
];

const PUBLIC_SETTINGS_TTL_MS = 60_000;
let cache = { settings: null, expiresAt: 0 };

async function fetchPublicSettings() {
  if (Date.now() < cache.expiresAt && cache.settings) return cache.settings;
  try {
    const resp = await fetch('/api/public/settings');
    if (!resp.ok) return {};
    const data = await resp.json();
    cache = { settings: data.settings || {}, expiresAt: Date.now() + PUBLIC_SETTINGS_TTL_MS };
    return cache.settings;
  } catch {
    return {};
  }
}

export function useVerticalAccent() {
  const location = useLocation();
  const [accent, setAccent] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const vertical = detectVertical(location.pathname);
      const settings = await fetchPublicSettings();
      const overrides = settings.vertical_accents || {};

      const override = vertical ? overrides[vertical] : null;
      if (cancelled) return;

      const root = document.documentElement;
      if (override) {
        root.style.setProperty('--snt-vertical-accent', override);
        setAccent(override);
      } else {
        root.style.removeProperty('--snt-vertical-accent');
        setAccent(null);
      }
    })();

    return () => { cancelled = true; };
  }, [location.pathname]);

  return accent;
}

function detectVertical(path) {
  for (const { match, vertical } of VERTICAL_PATTERNS) {
    if (match.test(path)) return vertical;
  }
  return null;
}
